// --------------------------------------------------------------
// Created On: 2022-01-17
// Author: Zachary Thomas
//
// Last Modified: 2026-09-29
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import apiRequest from "../../../utilities/api/apiRequest";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import formatDateLocal from "../../../utilities/time/formatDateLocal";
import useReduxSelector from "../../../hooks/useReduxSelector";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { API } from "../../../constants/miscellaneous";
import styles from "./NotificationTable.module.scss";
import { Link } from "react-router-dom";
import {
  toggleNotification,
  toggleAllNotifications,
  deleteNotification,
  deleteAllNotifications,
} from "src/redux/features/notificationsSlice";

// Table with notification data.
export default function NotificationTable(props: Props): Component {
  const [allChecked, setAllChecked] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const notifications = useReduxSelector((state) => state.notifications);
  const dispatch = useReduxDispatch();

  // Check if all notifications have already been seen.
  useEffect(() => {
    let tempAllChecked = true;
    notifications.forEach((notification: Notification) => {
      if (notification.unread) {
        tempAllChecked = false;
      }
    });
    setAllChecked(tempAllChecked);
  }, [JSON.stringify(notifications)]);

  // Toggle seen status of a single notification.
  async function toggleNotificationSeen(alertMessageId: number, unread: boolean): Promise<void> {
    dispatch(toggleNotification(alertMessageId));
    const requestBody = { alertMessageIds: [alertMessageId], unread: !unread };
    void apiRequest(`${API}/alertmessage`, "PATCH", requestBody);
  }

  // Toggle seen status of all notifications.
  async function toggleAllNotificationsSeen(): Promise<void> {
    dispatch(toggleAllNotifications());
    const alertMessageIds: number[] = [];
    notifications.forEach((notification) => alertMessageIds.push(notification.alertMessageId));
    const requestBody = { alertMessageIds: alertMessageIds, unread: allChecked };
    void apiRequest(`${API}/alertmessage`, "PATCH", requestBody);
  }

  // Delete a single notification.
  async function deleteNotificationMessage(alertMessageId: number): Promise<void> {
    dispatch(deleteNotification(alertMessageId));
    const requestBody = { alertMessageIds: [alertMessageId] };
    void apiRequest(`${API}/alertmessage`, "DELETE", requestBody);
  }

  // Delete all notifications.
  async function deleteAllNotificationMessages(): Promise<void> {
    dispatch(deleteAllNotifications());
    setShowConfirmDelete(false);
    const alertMessageIds: number[] = [];
    notifications.forEach((notification) => alertMessageIds.push(notification.alertMessageId));
    const requestBody = { alertMessageIds: alertMessageIds };
    void apiRequest(`${API}/alertmessage`, "DELETE", requestBody);
  }

  return !props.loading ? (
    <div className={`${styles.body} shadow-sm mt-5`} data-test="alert-details">
      <div className={`${styles.header} p-4 text-center`}>Notifications ({notifications.length})</div>

      {notifications.length > 0 ? (
        <div className="table-responsive">
          <table className={`${styles.notificationTable} table mb-0 pb-0`}>
            <thead>
              <tr>
                <th>Message</th>
                <th>Date</th>
                <th>Link to Page</th>
                <th>
                  <input
                    className="form-check-input form-check-big"
                    type="checkbox"
                    checked={allChecked}
                    onChange={() => toggleAllNotificationsSeen()}
                  />
                </th>
                <th>
                  <i
                    className={`${styles.deleteButton} fa fa-fw fa-times`}
                    onClick={() => setShowConfirmDelete(true)}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification: Notification) => (
                <tr className="list-item py-2" key={notification.alertMessageId} data-test="alerts-table-row">
                  <td className={`align-middle ${notification.unread ? "" : styles.messageRead}`}>
                    <span>{notification.message}</span>
                  </td>

                  <td className={`align-middle ${notification.unread ? "" : styles.messageRead}`}>
                    {formatDateLocal(notification.date)}
                  </td>

                  <td className={`align-middle ${notification.unread ? "" : styles.messageRead}`}>
                    {notification.alertUrl !== null && notification.alertUrl.length > 0 ? (
                      <Link to={notification.alertUrl} className="router-link">
                        <i className="fa fa-fw fa-external-link" />
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-check-input form-check-big"
                      type="checkbox"
                      checked={!notification.unread}
                      onChange={() => toggleNotificationSeen(notification.alertMessageId, notification.unread)}
                    />
                  </td>

                  <td className="align-middle">
                    <i
                      className={`${styles.deleteButton} fa fa-fw fa-times`}
                      onClick={() => deleteNotificationMessage(notification.alertMessageId)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={`${styles.emptyList} py-5 px-4`}>You do not have any notifications to view.</div>
      )}

      <ConfirmModal
        showModal={showConfirmDelete}
        title="Delete all notifications?"
        content={
          "Are you sure that you want to delete all notifications? This action cannot be undone. You can mark notifications as 'seen' instead if you want to save them."
        }
        yesText="Delete All"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => deleteAllNotificationMessages()}
        onNo={() => setShowConfirmDelete(false)}
      />
    </div>
  ) : null;
}

interface Props {
  loading: boolean;
}

interface Notification {
  alertMessageId: number;
  alertCode: string;
  date: string;
  message: string;
  unread: boolean;
  urgency: string;
  alertUrl: string;
}
